import { useState } from "react";
import { Box, Typography, Colors, Collapse } from "@analyticsMD/quil";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { cx } from "@emotion/css";
import SectionGrid from "../section-grid";
import {
  styles as commonStyles,
  sxStyles as commonSxStyles,
} from "../sections/common-styles";

import type { FC, ReactNode, SyntheticEvent } from "react";

interface CollapsibleSectionProps {
  title: ReactNode;
  icon?: ReactNode;
  summary?: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  /** Wrap body in SectionGrid for shared horizontal padding. Default: true. */
  wrapBodyInGrid?: boolean;
  /** Optional className for the outer Box (e.g., to set background). */
  outerClassName?: string;
  /** Optional sx override for the heading wrapper. */
  headingSx?: Record<string, unknown>;
  /** Optional sx override for the heading typography. */
  headingTextSx?: Record<string, unknown>;
  /** Optional render override for unmountOnExit (set to false to keep children mounted). */
  unmountOnExit?: boolean;
  /** Optional test id passthrough. */
  dataTestId?: string;
}

const CollapsibleSection: FC<CollapsibleSectionProps> = ({
  title,
  icon,
  summary,
  children,
  defaultOpen = true,
  wrapBodyInGrid = true,
  outerClassName,
  headingSx,
  headingTextSx,
  unmountOnExit = true,
  dataTestId,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  const toggle = (event: SyntheticEvent) => {
    event.stopPropagation();
    setOpen((prev) => !prev);
  };

  return (
    <Box
      className={cx(commonStyles.roundedSection, outerClassName)}
      data-testid={dataTestId}
    >
      <Box
        sx={{ ...commonSxStyles.headingWrapper, cursor: "pointer", ...headingSx }}
        onClick={toggle}
        role="button"
        aria-expanded={open}
      >
        <Typography sx={{ ...commonSxStyles.sectionHeading, ...headingTextSx }}>
          {icon}
          <span>{title}</span>
          {summary && (
            <Box component="span" sx={{ marginLeft: "0.5rem", fontWeight: 400, color: Colors.grey_40 }}>
              {summary}
            </Box>
          )}
        </Typography>
        <Box>
          {open ? (
            <ExpandLessIcon sx={{ color: Colors.grey_40 }} />
          ) : (
            <ExpandMoreIcon sx={{ color: Colors.grey_40 }} />
          )}
        </Box>
      </Box>
      <Collapse in={open} unmountOnExit={unmountOnExit}>
        {wrapBodyInGrid ? <SectionGrid>{children}</SectionGrid> : children}
      </Collapse>
    </Box>
  );
};

export default CollapsibleSection;
